import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Share2, Copy, ExternalLink, DollarSign, Wallet, Clock, CheckCircle, XCircle, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const commStatusColor = (s) => {
  if (s === 'paid') return 'bg-emerald-100 text-emerald-700';
  if (s === 'approved') return 'bg-blue-100 text-blue-700';
  if (s === 'canceled') return 'bg-red-100 text-red-700';
  return 'bg-amber-100 text-amber-700';
};

export default function CustomerAffiliate() {
  const [data, setData] = useState({ affiliate: null, commissions: [], stats: {} });
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankForm, setBankForm] = useState({ bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [registering, setRegistering] = useState(false);

  const fetchData = async () => {
    try {
      const [affRes, wdRes] = await Promise.all([
        api.get('/customer/affiliate'),
        api.get('/customer/affiliate/withdrawals').catch(() => ({ data: { withdrawals: [] } }))
      ]);
      setData(affRes.data);
      setWithdrawals(wdRes.data.withdrawals || []);
      if (affRes.data.affiliate) {
        setBankForm({
          bank_name: affRes.data.affiliate.bank_name || '',
          bank_account_number: affRes.data.affiliate.bank_account_number || '',
          bank_account_name: affRes.data.affiliate.bank_account_name || ''
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await api.post('/customer/affiliate/register');
      toast.success('Berhasil mendaftar sebagai affiliate!');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mendaftar');
    } finally {
      setRegistering(false);
    }
  };

  const handleSaveBank = async () => {
    if (!bankForm.bank_name || !bankForm.bank_account_number || !bankForm.bank_account_name) {
      toast.error('Lengkapi semua data bank'); return;
    }
    setSavingBank(true);
    try {
      await api.put('/customer/affiliate/bank', bankForm);
      toast.success('Data bank disimpan');
      fetchData();
    } catch (e) {
      toast.error('Gagal menyimpan data bank');
    } finally {
      setSavingBank(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseInt(withdrawAmount);
    if (!amount || amount < 1000) { toast.error('Minimum withdrawal Rp 1.000'); return; }
    setWithdrawing(true);
    try {
      await api.post('/customer/affiliate/withdraw', { amount });
      toast.success('Permintaan withdrawal berhasil dikirim');
      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal request withdrawal');
    } finally {
      setWithdrawing(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link berhasil disalin!');
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  const aff = data.affiliate;
  const commissions = data.commissions || [];
  const approvedBalance = commissions
    .filter(c => c.status === 'approved')
    .reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div data-testid="customer-affiliate-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Affiliate Program</h1>
        <p className="text-sm text-slate-500 mt-1">Dapatkan komisi dengan merekomendasikan produk</p>
      </div>

      {!aff ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Share2 size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-semibold text-slate-900 mb-2">Belum terdaftar sebagai Affiliate</h3>
            <p className="text-sm text-slate-500 mb-6">Daftarkan diri Anda untuk mendapatkan link referral dan komisi dari setiap penjualan</p>
            <Button onClick={handleRegister} disabled={registering} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {registering ? <><Loader2 size={16} className="mr-2 animate-spin" /> Mendaftar...</> : <><Share2 size={16} className="mr-2" /> Daftar Affiliate</>}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="commissions">Komisi ({commissions.length})</TabsTrigger>
            <TabsTrigger value="withdrawal">Withdrawal</TabsTrigger>
            <TabsTrigger value="bank">Data Bank</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview">
            {/* Status Badge */}
            <div className="mb-4 flex items-center gap-3">
              <Badge className={aff.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                {aff.status === 'active' ? 'Aktif' : aff.status}
              </Badge>
              <span className="text-sm text-slate-500">Kode: <span className="font-mono font-semibold text-slate-800">{aff.code}</span></span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Klik', value: aff.total_clicks || 0, icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-50' },
                { label: 'Konversi', value: aff.total_conversions || 0, icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                { label: 'Pending', value: `Rp ${fmt(commissions.filter(c => c.status === 'pending').reduce((s, c) => s + c.amount, 0))}`, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' },
                { label: 'Bisa Dicairkan', value: `Rp ${fmt(approvedBalance)}`, icon: Wallet, color: 'text-violet-500', bg: 'bg-violet-50' },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <Card key={label} className="border-slate-200 shadow-sm">
                  <CardContent className="p-4">
                    <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-2`}>
                      <Icon size={16} className={color} />
                    </div>
                    <p className="text-xl font-bold text-slate-900">{value}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Referral Link */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Link Referral Anda</h3>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={aff.referral_link || `${window.location.origin}/store/${aff.tenant_subdomain || ''}?ref=${aff.code}`}
                    className="font-mono text-sm bg-slate-50"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyLink(aff.referral_link || `${window.location.origin}/store/${aff.tenant_subdomain || ''}?ref=${aff.code}`)}
                  >
                    <Copy size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(aff.referral_link, '_blank')}
                  >
                    <ExternalLink size={16} />
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">Setiap pembelian melalui link ini akan memberikan komisi untuk Anda</p>
              </CardContent>
            </Card>

            {approvedBalance > 0 && (
              <div className="mt-4">
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setShowWithdrawDialog(true)}
                >
                  <Wallet size={16} className="mr-2" /> Cairkan Komisi (Rp {fmt(approvedBalance)})
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Commissions */}
          <TabsContent value="commissions">
            {commissions.length === 0 ? (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="py-12 text-center">
                  <DollarSign size={40} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Belum ada komisi</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {commissions.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{c.description || c.order_id?.slice(0, 12)}</p>
                      <p className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString('id-ID')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-emerald-600">Rp {fmt(c.amount)}</span>
                      <Badge className={commStatusColor(c.status)}>{c.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Withdrawal */}
          <TabsContent value="withdrawal">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-slate-600">Saldo tersedia: <span className="font-bold text-emerald-600">Rp {fmt(approvedBalance)}</span></p>
              <Button
                size="sm"
                onClick={() => setShowWithdrawDialog(true)}
                disabled={approvedBalance < 1000}
                className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]"
              >
                <Wallet size={16} className="mr-2" /> Cairkan
              </Button>
            </div>
            {withdrawals.length === 0 ? (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Wallet size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">Belum ada riwayat withdrawal</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {withdrawals.map(w => (
                  <div key={w.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Rp {fmt(w.amount)}</p>
                      <p className="text-xs text-slate-400">{new Date(w.created_at).toLocaleDateString('id-ID')}</p>
                      {w.bank_name && <p className="text-xs text-slate-500">{w.bank_name} · {w.bank_account_number}</p>}
                    </div>
                    <Badge className={w.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : w.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                      {w.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bank Data */}
          <TabsContent value="bank">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base">Data Rekening untuk Withdrawal</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Nama Bank</Label>
                  <Input value={bankForm.bank_name} onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))} placeholder="BCA, Mandiri, BNI, BSI..." className="mt-1" />
                </div>
                <div>
                  <Label>Nomor Rekening</Label>
                  <Input value={bankForm.bank_account_number} onChange={e => setBankForm(p => ({ ...p, bank_account_number: e.target.value }))} placeholder="1234567890" className="mt-1" />
                </div>
                <div>
                  <Label>Nama Pemilik Rekening</Label>
                  <Input value={bankForm.bank_account_name} onChange={e => setBankForm(p => ({ ...p, bank_account_name: e.target.value }))} placeholder="Sesuai buku tabungan" className="mt-1" />
                </div>
                <Button onClick={handleSaveBank} disabled={savingBank} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
                  {savingBank ? <><Loader2 size={16} className="mr-2 animate-spin" /> Menyimpan...</> : 'Simpan Data Bank'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Withdraw Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Withdrawal</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">Saldo tersedia: <span className="font-bold text-emerald-600">Rp {fmt(approvedBalance)}</span></p>
            <div>
              <Label>Jumlah Penarikan</Label>
              <Input
                type="number"
                placeholder="Minimal Rp 1.000"
                value={withdrawAmount}
                onChange={e => setWithdrawAmount(e.target.value)}
                max={approvedBalance}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>Batal</Button>
            <Button onClick={handleWithdraw} disabled={withdrawing} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {withdrawing ? <><Loader2 size={16} className="mr-2 animate-spin" /> Memproses...</> : 'Ajukan Withdrawal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
